/**
 * The payment service: batches, obligations, attempts, salary holds and off-cycle payments (ADR 0015).
 *
 *     released run / approved off-cycle / approved F&F
 *        ──▶ obligations ──prepare──▶ batch ──submit──▶ pending_authorisation ──authorise──▶ authorised
 *                                                         ├── export bank file ──▶ confirm upload ──▶ submitted
 *                                                         └── transmit (worker) ─────────────────▶ submitted
 *                                                                                   ──▶ completed (reconciled)
 *
 * CONTRACT FIRST (CLAUDE.md invariant 8). This file declares the routes and their schemas; nothing
 * implements them yet. The state machines they move are in `../payroll-payments.ts`.
 *
 * PREPARE, AUTHORISE, TRANSMIT AND RECONCILE ARE FOUR PERMISSIONS, HELD BY DIFFERENT PEOPLE.
 *   - `payroll.payment:read`       the payment desk and its auditors see batches, obligations, attempts.
 *   - `payroll.payment:prepare`    payroll administration assembles a batch, submits it, cancels it.
 *   - `payroll.payment:authorise`  the owner authorises the EXACT digest of a batch version. Because the owner
 *                                  inherits every payroll-admin permission, the API also refuses the person
 *                                  who prepared or submitted that content version (maker-checker at runtime).
 *   - `payroll.payment:transmit`   payroll administration generates the bank file, records its upload,
 *                                  sends a batch to a provider, retries a definitive failure, asks a provider
 *                                  for a status.
 *   - `payroll.payment:reconcile`  finance or the owner records an outcome the system did not see, with
 *                                  evidence. Never the preparer.
 *   - `payroll.payment.team:read`  a manager sees their team's payment STATUS — no amount, no bank field,
 *                                  no payslip figure.
 *   - `payroll.payment.own:read`   an employee sees their own payments.
 *   - `payroll.hold:request | manage | approve` and `payroll.offcycle:manage | approve` likewise split the
 *     request, the preparation and the owner's approval.
 * Each route declares ONE permission, except the hold list/detail (read by the desk AND by requesting
 * managers, scoped in the handler) and hold withdrawal (by the requester or the desk). The copilot's tool
 * list is filtered by these, and every write is a proposal executed on the person's click.
 *
 * EVERY WRITE TAKES `Idempotency-Key` (invariant 10), and every write that moves a batch, a hold or an
 * off-cycle payment takes that record's `expectedVersion`: a record that moved on is a 409, a retried key
 * returns the original result.
 *
 * NO FULL ACCOUNT NUMBER APPEARS ANYWHERE HERE. A destination is its bank, its bank code (IFSC / sort
 * code), the last four digits and a masked VPA. The bank-file download link and the ledger export are
 * `ai: 'hidden'`: a file that carries account numbers is never pulled into a prompt.
 *
 * WHAT THIS DOES NOT DO. It carries no provider credentials (a connection lists its capabilities, never its
 * keys), accepts no provider callback (those arrive on an authenticated webhook outside this contract),
 * publishes no payslip (the lifecycle contract does), and changes no bank account (an employee's change goes
 * through `POST /employees/profile-requests`, category 'Bank Account', and then needs a new batch and a new
 * authorisation). Nothing here marks a payment paid without a provider outcome or reconciliation evidence.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CURRENCIES } from '../enums.js';
import { PAYSLIP_STATUSES } from '../payroll-lifecycle.js';
import {
  OFF_CYCLE_CATEGORIES,
  OFF_CYCLE_STATES,
  PAYMENT_ATTEMPT_STATES,
  PAYMENT_BATCH_STATES,
  PAYMENT_CHANNELS,
  PAYMENT_OBLIGATION_STATES,
  PAYMENT_SOURCE_TYPES,
  SALARY_HOLD_REASONS,
  SALARY_HOLD_STATES,
  type ExclusionReason,
} from '../payroll-payments.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  sha256HexSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const currencySchema = z.enum(CURRENCIES);
const expectedVersionSchema = z.number().int().positive();
const countSchema = z.number().int().nonnegative();
const batchParams = z.object({ batchId: uuidSchema });
const obligationParams = z.object({ obligationId: uuidSchema });
const holdParams = z.object({ holdId: uuidSchema });
const offCycleParams = z.object({ offCycleId: uuidSchema });

/** A reason a person must give: said, not a single character. */
const reasonSchema = z.string().trim().min(5).max(1000);
const optionalNoteSchema = z.string().trim().max(1000).optional();

/** A money amount strictly greater than zero (never a negative or a zero line). */
export const positiveAmountSchema = decimalStringSchema.refine(
  (v) => !v.startsWith('-') && /[1-9]/.test(v),
  'Must be greater than zero',
);

export const paymentChannelSchema = z.enum(PAYMENT_CHANNELS);
export const paymentSourceTypeSchema = z.enum(PAYMENT_SOURCE_TYPES);
export const paymentBatchStateSchema = z.enum(PAYMENT_BATCH_STATES);
export const paymentObligationStateSchema = z.enum(PAYMENT_OBLIGATION_STATES);
export const paymentAttemptStateSchema = z.enum(PAYMENT_ATTEMPT_STATES);
export const salaryHoldStateSchema = z.enum(SALARY_HOLD_STATES);
export const salaryHoldReasonSchema = z.enum(SALARY_HOLD_REASONS);
export const salaryHoldScopeSchema = z.enum(['next_payment', 'until_released']);
export const offCycleStateSchema = z.enum(OFF_CYCLE_STATES);
export const offCycleCategorySchema = z.enum(OFF_CYCLE_CATEGORIES);

/** Every `ExclusionReason` of the state model, as data. The two checks below keep them equal. */
export const PAYMENT_EXCLUSION_REASONS = [
  'held',
  'already_paid',
  'in_other_batch',
  'in_flight',
  'currency_mismatch',
  'no_destination',
  'zero_amount',
  'negative_amount',
  'cancelled',
] as const satisfies readonly ExclusionReason[];
type MissingExclusionReason = Exclude<ExclusionReason, (typeof PAYMENT_EXCLUSION_REASONS)[number]>;
const _everyExclusionReasonListed: MissingExclusionReason extends never ? true : false = true;
void _everyExclusionReasonListed;

const decisionSchema = z.enum(['approved', 'rejected']);

/** A page of rows, numbered like the lifecycle outputs. */
const pageQueryShape = {
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
};
function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: countSchema,
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
  });
}

/** A rejection carries its reason. */
function requireCommentOnReject(
  b: { decision: 'approved' | 'rejected'; comment?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (b.decision === 'rejected' && !b.comment?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['comment'],
      message: 'Say why: a rejection is recorded with a reason',
    });
  }
}

// ---------------------------------------------------------------------------
// Shared read shapes
// ---------------------------------------------------------------------------

/**
 * Where a payment goes, MASKED. There is no full account number here, and no holder name: the bank, its
 * code (IFSC / sort code), the last four digits, or a masked VPA for UPI.
 */
export const paymentDestinationSchema = z.object({
  channel: paymentChannelSchema,
  bankName: z.string().nullable(),
  /** IFSC in India, sort code in the UK. */
  bankCode: z.string().nullable(),
  accountLast4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable(),
  /** e.g. `an***@okaxis`; only for `upi_payout`. */
  vpaMasked: z.string().nullable(),
});
export type PaymentDestination = z.infer<typeof paymentDestinationSchema>;

/** The ledger record an obligation traces to. */
export const paymentSourceRefSchema = z.object({
  type: paymentSourceTypeSchema,
  id: uuidSchema,
  /** e.g. "September 2026 payroll", "Bonus — off-cycle", "Full & Final". */
  label: z.string(),
});
export type PaymentSourceRef = z.infer<typeof paymentSourceRefSchema>;

/** What a batch was assembled from: a payroll run, or an off-cycle payment / F&F settlement. */
export const paymentBatchSourceSchema = z.object({
  type: z.enum(['payroll_run', 'off_cycle', 'fnf_settlement']),
  id: uuidSchema,
  label: z.string(),
});

/** Obligations per state, for the whole selection — not just a page. */
export const paymentObligationCountsSchema = z.object({
  open: countSchema,
  held: countSchema,
  batched: countSchema,
  in_flight: countSchema,
  paid: countSchema,
  failed: countSchema,
  cancelled: countSchema,
});

/** A server-computed yes/no for one action, with the reason when it is no. */
export const paymentActionSchema = z.object({
  allowed: z.boolean(),
  reason: z.string().nullable(),
});

export const paymentBatchSummarySchema = z.object({
  id: uuidSchema,
  version: z.number().int().positive(),
  state: paymentBatchStateSchema,
  channel: paymentChannelSchema,
  currency: currencySchema,
  beneficiaryCount: countSchema,
  netTotal: decimalStringSchema,
  preparedAt: instantSchema,
  preparedByName: z.string(),
});
export type PaymentBatchSummary = z.infer<typeof paymentBatchSummarySchema>;

// ---------------------------------------------------------------------------
// Overview and providers
// ---------------------------------------------------------------------------

export const paymentsOverviewQuerySchema = z.object({
  legalEntityId: uuidSchema,
  runId: uuidSchema.optional(),
});

export const paymentReleasedRunSchema = z.object({
  id: uuidSchema,
  periodLabel: z.string(),
  payGroup: z.object({ id: uuidSchema, name: z.string() }),
  currency: currencySchema,
  payDate: dateOnlySchema,
  payableCount: countSchema,
  netTotal: decimalStringSchema,
});

/**
 * Credited obligations out of RELEASABLE ones (held and cancelled excluded). A success proportion,
 * labelled "Credited share" — it is not a service level. `null` when nothing is releasable.
 */
export const creditedShareSchema = z.object({
  label: z.literal('Credited share'),
  numerator: countSchema,
  denominator: z.number().int().positive(),
  definition: z.string(),
});

export const paymentsOverviewSchema = z.object({
  legalEntityId: uuidSchema,
  /** RELEASED runs of the entity that have payable results. */
  runs: z.array(paymentReleasedRunSchema),
  selectedRunId: uuidSchema.nullable(),
  selected: z
    .object({
      runId: uuidSchema,
      currency: currencySchema,
      /** The run's outputs were moved manually (lifecycle `recordPaymentTransition`): not paid through here. */
      manuallyTracked: z.boolean(),
      counts: paymentObligationCountsSchema,
      creditedShare: creditedShareSchema.nullable(),
      totals: z.object({
        credited: decimalStringSchema,
        held: decimalStringSchema,
        failed: decimalStringSchema,
        processing: decimalStringSchema,
      }),
      activeBatch: paymentBatchSummarySchema.nullable(),
    })
    .nullable(),
});
export type PaymentsOverview = z.infer<typeof paymentsOverviewSchema>;

/** A configured provider or bank connection. Its credentials never leave the server. */
export const paymentProviderConnectionSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  adapterId: z.string(),
  capabilities: z.object({
    channels: z.array(paymentChannelSchema),
    statusQuery: z.boolean(),
    cancellation: z.boolean(),
    callbacks: z.boolean(),
  }),
  status: z.enum(['active', 'disabled', 'unconfigured']),
  /** The legal entity the connection pays from; null only for the unconfigured placeholders. */
  legalEntityId: uuidSchema.nullable(),
  /** The company debit account, masked to its last four digits (e.g. `XXXXXXXX1234`). */
  debitAccountMasked: z.string().nullable(),
  /** The bank's product / payment-type code where its file format carries one. */
  productTypeCode: z.string().nullable(),
  /** The adapter's file format needs the company debit account (ICICI CIB). */
  requiresDebitAccount: z.boolean(),
});
export type PaymentProviderConnection = z.infer<typeof paymentProviderConnectionSchema>;

export const paymentAdapterOptionSchema = z.object({
  adapterId: z.string(),
  label: z.string(),
  channels: z.array(paymentChannelSchema),
  requiresDebitAccount: z.boolean(),
});
export type PaymentAdapterOption = z.infer<typeof paymentAdapterOptionSchema>;

export const paymentProviderListSchema = z.object({
  items: z.array(paymentProviderConnectionSchema),
  /** The adapters a new connection may use. */
  availableAdapters: z.array(paymentAdapterOptionSchema),
});

const providerParams = z.object({ providerId: uuidSchema });
const debitAccountNumberSchema = z
  .string()
  .trim()
  .regex(/^[0-9]{6,34}$/, 'An account number is 6 to 34 digits');
const providerDisplayNameSchema = z.string().trim().min(1).max(120);

/** Configure a bank or provider connection for a legal entity. Credentials are never set here. */
export const createPaymentProviderBodySchema = z.object({
  legalEntityId: uuidSchema,
  adapterId: z.string().min(1).max(64),
  displayName: providerDisplayNameSchema,
  debitAccountNumber: debitAccountNumberSchema.optional(),
  productTypeCode: z.string().trim().max(50).optional(),
});
export type CreatePaymentProviderBody = z.infer<typeof createPaymentProviderBodySchema>;

export const updatePaymentProviderBodySchema = z
  .object({
    displayName: providerDisplayNameSchema.optional(),
    status: z.enum(['active', 'disabled']).optional(),
    debitAccountNumber: debitAccountNumberSchema.nullable().optional(),
    productTypeCode: z.string().trim().max(50).nullable().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), 'Change at least one field');
export type UpdatePaymentProviderBody = z.infer<typeof updatePaymentProviderBodySchema>;

// ---------------------------------------------------------------------------
// Batches
// ---------------------------------------------------------------------------

export const listPaymentBatchesQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  runId: uuidSchema.optional(),
  state: paymentBatchStateSchema.optional(),
  ...pageQueryShape,
});

export const paymentBatchListSchema = pageOf(paymentBatchSummarySchema);

export const paymentBankFileSchema = z.object({
  id: uuidSchema,
  /** 1 for the batch's first file; a re-export after a void is a new version. */
  version: z.number().int().positive(),
  /** The agreed format of the bank or provider, e.g. `hdfc_neft_csv`. */
  format: z.string(),
  fileName: z.string(),
  checksum: sha256HexSchema,
  createdAt: instantSchema,
  createdByName: z.string(),
  downloads: z.array(z.object({ byName: z.string(), at: instantSchema })),
  /** Downloaded is not paid: the upload is recorded separately, with the bank's reference. */
  uploadConfirmation: z
    .object({ bankReference: z.string(), uploadedAt: instantSchema, byName: z.string() })
    .nullable(),
  voidedAt: instantSchema.nullable(),
  voidReason: z.string().nullable(),
});
export type PaymentBankFile = z.infer<typeof paymentBankFileSchema>;

export const paymentBatchDetailSchema = z.object({
  id: uuidSchema,
  /** The batch ROW version: what `expectedVersion` names. */
  version: z.number().int().positive(),
  /** The CONTENT version the digest covers; authorisation binds to it. */
  contentVersion: z.number().int().positive(),
  contentDigest: sha256HexSchema,
  state: paymentBatchStateSchema,
  channel: paymentChannelSchema,
  providerConnectionId: uuidSchema,
  legalEntityId: uuidSchema,
  currency: currencySchema,
  sources: z.array(paymentBatchSourceSchema),
  beneficiaryCount: countSchema,
  /** "Net Amount for Disbursement": the sum of the batch's obligations, in `currency`. */
  netTotal: decimalStringSchema,
  /** Candidates left out at preparation, per reason. */
  excluded: z.object({
    held: countSchema,
    already_paid: countSchema,
    in_other_batch: countSchema,
    in_flight: countSchema,
    currency_mismatch: countSchema,
    no_destination: countSchema,
    zero_amount: countSchema,
    negative_amount: countSchema,
    cancelled: countSchema,
  }),
  preparedByName: z.string(),
  preparedAt: instantSchema,
  submittedByName: z.string().nullable(),
  submittedAt: instantSchema.nullable(),
  authorisation: z
    .object({
      decision: decisionSchema,
      byName: z.string(),
      at: instantSchema,
      /** The digest that was authorised; it equals `contentDigest` or the batch is invalidated. */
      digest: sha256HexSchema,
      comment: z.string().nullable(),
      stepUpMethod: z.enum(['totp']).nullable(),
    })
    .nullable(),
  files: z.array(paymentBankFileSchema),
  /** Computed by the server for the caller, including maker-checker refusals. */
  actions: z.object({
    canSubmit: paymentActionSchema,
    canAuthorise: paymentActionSchema,
    canExport: paymentActionSchema,
    canTransmit: paymentActionSchema,
    canCancel: paymentActionSchema,
    canConfirmUpload: paymentActionSchema,
    canVoidExport: paymentActionSchema,
  }),
});
export type PaymentBatchDetail = z.infer<typeof paymentBatchDetailSchema>;

/**
 * Assemble a batch. From a RELEASED payroll run: every payable, unheld, unclaimed result. From named
 * obligations: a retry batch (a changed destination), an off-cycle payment or an F&F settlement. Each
 * beneficiary's destination is SNAPSHOTTED now; transmission never re-reads the live bank account.
 */
export const preparePaymentBatchSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('payroll_run'), runId: uuidSchema }),
  z.object({
    type: z.literal('obligations'),
    obligationIds: z.array(uuidSchema).min(1).max(5000),
  }),
]);

export const preparePaymentBatchBodySchema = z.object({
  legalEntityId: uuidSchema,
  currency: currencySchema,
  channel: paymentChannelSchema,
  providerConnectionId: uuidSchema,
  source: preparePaymentBatchSourceSchema,
});
export type PreparePaymentBatchBody = z.infer<typeof preparePaymentBatchBodySchema>;

/** Send a prepared batch for authorisation, naming the digest the preparer reviewed. */
export const submitPaymentBatchBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  contentDigest: sha256HexSchema,
});
export type SubmitPaymentBatchBody = z.infer<typeof submitPaymentBatchBodySchema>;

/**
 * Authorise or reject the EXACT digest. The step-up code is verified server-side (TOTP via `@hrms/auth`);
 * whether it is required is the server's decision, and a six-digit field is not itself proof.
 */
export const authorisePaymentBatchBodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    contentDigest: sha256HexSchema,
    decision: decisionSchema,
    comment: z.string().trim().max(2000).optional(),
    stepUp: z.object({ method: z.literal('totp'), code: z.string().regex(/^\d{6}$/) }).optional(),
  })
  .superRefine(requireCommentOnReject);
export type AuthorisePaymentBatchBody = z.infer<typeof authorisePaymentBatchBodySchema>;

export const cancelPaymentBatchBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: reasonSchema,
});

export const batchVersionBodySchema = z.object({ expectedVersion: expectedVersionSchema });

/** Export a bank file; `valueDate` defaults to today in the legal entity's timezone. */
export const exportBankFileBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  valueDate: dateOnlySchema.optional(),
});
export type ExportBankFileBody = z.infer<typeof exportBankFileBodySchema>;

export const exportBankFileResultSchema = z.object({
  batch: paymentBatchDetailSchema,
  file: paymentBankFileSchema,
});

const bankFileParams = z.object({ batchId: uuidSchema, fileId: uuidSchema });

/** A short-lived private link. The file carries account numbers; the download is recorded. */
export const paymentFileLinkSchema = z.object({
  url: z.string().url(),
  expiresAt: instantSchema,
});

/** The file was uploaded to the bank portal; the bank's reference says which submission. */
export const confirmBatchUploadBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  fileId: uuidSchema,
  bankReference: z.string().trim().min(1).max(100),
  uploadedAt: instantSchema,
});
export type ConfirmBatchUploadBody = z.infer<typeof confirmBatchUploadBodySchema>;

/**
 * Change channel away from an exported file: attest that it was NOT uploaded. The batch becomes
 * `invalidated`; its obligations need a new batch and a new authorisation.
 */
export const voidBatchExportBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  fileId: uuidSchema,
  attestation: z.literal('file_not_uploaded'),
  reason: reasonSchema,
});
export type VoidBatchExportBody = z.infer<typeof voidBatchExportBodySchema>;

// ---------------------------------------------------------------------------
// Obligations and attempts
// ---------------------------------------------------------------------------

export const listPaymentObligationsQuerySchema = z.object({
  batchId: uuidSchema.optional(),
  runId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  state: paymentObligationStateSchema.optional(),
  /** Name or employee code. */
  q: z.string().trim().max(100).optional(),
  ...pageQueryShape,
});

export const paymentAttemptSummarySchema = z.object({
  id: uuidSchema,
  channel: paymentChannelSchema,
  state: paymentAttemptStateSchema,
  providerReference: z.string().nullable(),
  /** UTR / trace number once the bank reports one. */
  bankReference: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  at: instantSchema,
});

export const paymentObligationRowSchema = z.object({
  id: uuidSchema,
  source: paymentSourceRefSchema,
  legalEntityId: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  personName: z.string(),
  designation: z.string().nullable(),
  amount: decimalStringSchema,
  currency: currencySchema,
  state: paymentObligationStateSchema,
  isHeld: z.boolean(),
  destination: paymentDestinationSchema.nullable(),
  latestAttempt: paymentAttemptSummarySchema.nullable(),
  /** ADR 0014 output track, for payroll-result obligations only. */
  payslipStatus: z.enum(PAYSLIP_STATUSES).nullable(),
});
export type PaymentObligationRow = z.infer<typeof paymentObligationRowSchema>;

export const paymentObligationListSchema = pageOf(paymentObligationRowSchema);

export const paymentAttemptSchema = z.object({
  id: uuidSchema,
  attemptNo: z.number().int().positive(),
  batchId: uuidSchema,
  channel: paymentChannelSchema,
  state: paymentAttemptStateSchema,
  providerReference: z.string().nullable(),
  bankReference: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  submittedAt: instantSchema.nullable(),
  settledAt: instantSchema.nullable(),
});

/**
 * One report about an attempt. `appliedState` is null when it was recorded as evidence only (an earlier
 * stage arriving late); `conflict` flags a definitive outcome contradicting another — for reconciliation.
 */
export const paymentAttemptEventSchema = z.object({
  id: uuidSchema,
  attemptId: uuidSchema,
  source: z.enum(['provider_callback', 'status_query', 'reconciliation', 'system']),
  reportedState: paymentAttemptStateSchema,
  appliedState: paymentAttemptStateSchema.nullable(),
  conflict: z.boolean(),
  receivedAt: instantSchema,
  evidence: z.object({
    bankReference: z.string().optional(),
    valueDate: dateOnlySchema.optional(),
    note: z.string().optional(),
  }),
});

/** `retryVerdict` of the state model, for the caller to see before trying. */
export const paymentRetryVerdictSchema = z.object({
  kind: z.enum(['retry_in_batch', 'needs_new_batch', 'refused']),
  reason: z
    .enum(['destination_changed', 'already_paid', 'in_flight', 'unresolved', 'not_failed', 'held'])
    .nullable(),
});

export const paymentObligationDetailSchema = paymentObligationRowSchema.extend({
  /** Every destination snapshot taken for this obligation, masked, newest first. */
  destinationSnapshots: z.array(
    z.object({ id: uuidSchema, takenAt: instantSchema, destination: paymentDestinationSchema }),
  ),
  /** The snapshot the active batch authorised, and the one the employee's account gives today. */
  authorisedDestinationSnapshotId: uuidSchema.nullable(),
  currentDestinationSnapshotId: uuidSchema.nullable(),
  attempts: z.array(paymentAttemptSchema),
  events: z.array(paymentAttemptEventSchema),
  holds: z.array(
    z.object({
      id: uuidSchema,
      state: salaryHoldStateSchema,
      reasonCategory: salaryHoldReasonSchema,
      scope: salaryHoldScopeSchema,
    }),
  ),
  retryVerdict: paymentRetryVerdictSchema,
});
export type PaymentObligationDetail = z.infer<typeof paymentObligationDetailSchema>;

/** Retry a definitively failed obligation to the destination its batch authorised. */
export const retryPaymentObligationBodySchema = z.object({
  /** The failed latest attempt the caller saw; a different latest attempt is a 409. */
  expectedAttemptId: uuidSchema,
  reason: reasonSchema,
});
export type RetryPaymentObligationBody = z.infer<typeof retryPaymentObligationBodySchema>;

export const paymentStatusCheckBodySchema = z.object({ attemptId: uuidSchema });

/**
 * Record an outcome the system did not see, with evidence. A credit needs the bank's reference.
 */
export const reconcilePaymentObligationBodySchema = z
  .object({
    attemptId: uuidSchema,
    outcome: z.enum(['succeeded', 'failed']),
    evidence: z.object({
      bankReference: z.string().trim().min(1).max(100).optional(),
      valueDate: dateOnlySchema,
      note: z.string().trim().min(5).max(1000),
    }),
  })
  .superRefine((b, ctx) => {
    if (b.outcome === 'succeeded' && !b.evidence.bankReference?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence', 'bankReference'],
        message: "A credit is reconciled against the bank's reference (UTR / trace number)",
      });
    }
  });
export type ReconcilePaymentObligationBody = z.infer<typeof reconcilePaymentObligationBodySchema>;

/**
 * The bank's response file for a batch, one row per employee. Each row is applied with the rules of
 * a single reconciliation; a row that cannot be applied is skipped with its reason, never fatal.
 */
export const importBankResponseBodySchema = z.object({
  rows: z
    .array(
      z.object({
        employeeCode: z.string().trim().min(1).max(50),
        outcome: z.enum(['succeeded', 'failed']),
        bankReference: z.string().trim().max(100).optional(),
        failureReason: z.string().trim().max(500).optional(),
        valueDate: dateOnlySchema,
      }),
    )
    .min(1)
    .max(5000),
  note: z.string().trim().max(1000).optional(),
});
export type ImportBankResponseBody = z.infer<typeof importBankResponseBodySchema>;

export const importBankResponseResultSchema = z.object({
  applied: countSchema,
  skipped: z.array(z.object({ employeeCode: z.string(), reason: z.string() })),
  batch: paymentBatchDetailSchema,
});
export type ImportBankResponseResult = z.infer<typeof importBankResponseResultSchema>;

export const exportPaymentLedgerQuerySchema = z.object({
  legalEntityId: uuidSchema,
  runId: uuidSchema.optional(),
  batchId: uuidSchema.optional(),
  format: z.literal('csv').default('csv'),
});

export const paymentLedgerExportSchema = z.object({
  url: z.string().url(),
  expiresAt: instantSchema,
  rowCount: countSchema,
});

// ---------------------------------------------------------------------------
// Team and own
// ---------------------------------------------------------------------------

/** The employee-facing status: coarser than the obligation state, and the same for a manager. */
export const employeePaymentStatusSchema = z.enum([
  'paid',
  'processing',
  'failed',
  'held',
  'not_started',
]);

export const teamPaymentsQuerySchema = z.object({
  runId: uuidSchema.optional(),
  ...pageQueryShape,
});

/** A team member's payment STATUS. Deliberately no amount, no bank field and no payslip figure. */
export const teamPaymentRowSchema = z.object({
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  periodLabel: z.string(),
  status: employeePaymentStatusSchema,
});
export type TeamPaymentRow = z.infer<typeof teamPaymentRowSchema>;

export const teamPaymentListSchema = pageOf(teamPaymentRowSchema);

export const myPaymentRowSchema = z.object({
  obligationId: uuidSchema,
  sourceLabel: z.string(),
  periodLabel: z.string(),
  amount: decimalStringSchema,
  currency: currencySchema,
  status: employeePaymentStatusSchema,
  lastUpdatedAt: instantSchema,
  /** Written for the employee ("The bank reported the account as closed"), never a raw provider code. */
  failureMessage: z.string().nullable(),
  /** A failure a bank change could fix: use `POST /employees/profile-requests`, category 'Bank Account'. */
  canRequestBankChange: z.boolean(),
  destination: paymentDestinationSchema.nullable(),
});
export type MyPaymentRow = z.infer<typeof myPaymentRowSchema>;

export const myPaymentListSchema = z.object({ items: z.array(myPaymentRowSchema) });

// ---------------------------------------------------------------------------
// Salary holds
// ---------------------------------------------------------------------------

export const listSalaryHoldsQuerySchema = z.object({
  state: salaryHoldStateSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  ...pageQueryShape,
});

/**
 * A salary hold. `heldAmount` and `heldCurrency` are NULLED by the server for a caller without
 * `payroll.payment:read` (a requesting manager sees the hold, not the salary).
 */
export const paymentSalaryHoldSchema = z.object({
  id: uuidSchema,
  version: z.number().int().positive(),
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  reasonCategory: salaryHoldReasonSchema,
  reasonText: z.string(),
  scope: salaryHoldScopeSchema,
  state: salaryHoldStateSchema,
  requestedByName: z.string(),
  requestedAt: instantSchema,
  preparedByName: z.string().nullable(),
  preparedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  releasedByName: z.string().nullable(),
  releasedAt: instantSchema.nullable(),
  affectedObligationCount: countSchema,
  heldAmount: decimalStringSchema.nullable(),
  heldCurrency: currencySchema.nullable(),
});
export type PaymentSalaryHold = z.infer<typeof paymentSalaryHoldSchema>;

export const paymentSalaryHoldListSchema = pageOf(paymentSalaryHoldSchema);

export const paymentSalaryHoldDetailSchema = paymentSalaryHoldSchema.extend({
  history: z.array(
    z.object({
      at: instantSchema,
      byName: z.string(),
      action: z.enum(['requested', 'prepared', 'approved', 'rejected', 'withdrawn', 'released']),
      note: z.string().nullable(),
    }),
  ),
});
export type PaymentSalaryHoldDetail = z.infer<typeof paymentSalaryHoldDetailSchema>;

export const requestSalaryHoldBodySchema = z.object({
  employmentId: uuidSchema,
  reasonCategory: salaryHoldReasonSchema,
  reasonText: z.string().trim().min(10).max(2000),
  scope: salaryHoldScopeSchema,
});
export type RequestSalaryHoldBody = z.infer<typeof requestSalaryHoldBodySchema>;

export const prepareSalaryHoldBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  note: optionalNoteSchema,
});

export const decideSalaryHoldBodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    decision: decisionSchema,
    comment: z.string().trim().max(2000).optional(),
  })
  .superRefine(requireCommentOnReject);
export type DecideSalaryHoldBody = z.infer<typeof decideSalaryHoldBodySchema>;

export const withdrawSalaryHoldBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: reasonSchema,
});

export const releaseSalaryHoldRequestSchema = z.object({
  expectedVersion: expectedVersionSchema,
  note: reasonSchema,
});

export const releaseSalaryHoldResultSchema = z.object({
  hold: paymentSalaryHoldDetailSchema,
  /** Held obligations re-assessed and returned to `open` (or left held by another active hold). */
  reassessedObligationCount: countSchema,
});

// ---------------------------------------------------------------------------
// Off-cycle payments
// ---------------------------------------------------------------------------

export const listOffCyclePaymentsQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  employmentId: uuidSchema.optional(),
  state: offCycleStateSchema.optional(),
  ...pageQueryShape,
});

export const offCyclePaymentSchema = z.object({
  id: uuidSchema,
  version: z.number().int().positive(),
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  legalEntityId: uuidSchema,
  category: offCycleCategorySchema,
  reason: z.string(),
  payDate: dateOnlySchema,
  currency: currencySchema,
  components: z.array(
    z.object({ componentCode: z.string(), componentName: z.string(), amount: decimalStringSchema }),
  ),
  state: offCycleStateSchema,
  /** The calculated version an approval binds to. Every figure here comes from the same result. */
  calculation: z
    .object({
      versionId: uuidSchema,
      gross: decimalStringSchema,
      taxDeducted: decimalStringSchema,
      statutoryEmployee: decimalStringSchema,
      otherDeductions: decimalStringSchema,
      netPayable: decimalStringSchema,
      calculatedAt: instantSchema,
      problems: z.array(z.object({ code: z.string(), message: z.string() })),
    })
    .nullable(),
  approval: z
    .object({
      decision: decisionSchema,
      byName: z.string(),
      at: instantSchema,
      comment: z.string().nullable(),
    })
    .nullable(),
  obligationId: uuidSchema.nullable(),
  /** The off-cycle payroll run it is calculated in; it enters year-to-date totals from there. */
  runId: uuidSchema.nullable(),
});
export type OffCyclePayment = z.infer<typeof offCyclePaymentSchema>;

export const offCyclePaymentListSchema = pageOf(offCyclePaymentSchema);

const offCycleComponentsSchema = z
  .array(
    z.object({ componentCode: z.string().trim().min(1).max(50), amount: positiveAmountSchema }),
  )
  .min(1)
  .max(50)
  .superRefine((items, ctx) => {
    const seen = new Set<string>();
    items.forEach((item, i) => {
      if (seen.has(item.componentCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'componentCode'],
          message: 'Each component appears once',
        });
      }
      seen.add(item.componentCode);
    });
  });

export const createOffCyclePaymentBodySchema = z.object({
  employmentId: uuidSchema,
  category: offCycleCategorySchema,
  reason: z.string().trim().min(10).max(2000),
  payDate: dateOnlySchema,
  currency: currencySchema,
  components: offCycleComponentsSchema,
});
export type CreateOffCyclePaymentBody = z.infer<typeof createOffCyclePaymentBodySchema>;

/** Edit a `draft` or `rejected` off-cycle payment; any change discards its calculation. */
export const updateOffCyclePaymentBodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    category: offCycleCategorySchema.optional(),
    reason: z.string().trim().min(10).max(2000).optional(),
    payDate: dateOnlySchema.optional(),
    currency: currencySchema.optional(),
    components: offCycleComponentsSchema.optional(),
  })
  .refine((b) => Object.keys(b).some((k) => k !== 'expectedVersion'), 'Change at least one field');
export type UpdateOffCyclePaymentBody = z.infer<typeof updateOffCyclePaymentBodySchema>;

export const offCycleVersionBodySchema = z.object({ expectedVersion: expectedVersionSchema });

export const submitOffCyclePaymentBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  calculationVersionId: uuidSchema,
});

export const decideOffCyclePaymentBodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    calculationVersionId: uuidSchema,
    decision: decisionSchema,
    comment: z.string().trim().max(2000).optional(),
  })
  .superRefine(requireCommentOnReject);
export type DecideOffCyclePaymentBody = z.infer<typeof decideOffCyclePaymentBodySchema>;

export const cancelOffCyclePaymentBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: reasonSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const readErrors = {
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
} as const;

/**
 * Static segments (`overview`, `providers`, `ledger/export`, `team`, `mine`, and the collection paths)
 * are declared before any parameterised sibling.
 */
export const payrollPaymentsContract = c.router(
  {
    // --- reads -------------------------------------------------------------

    getOverview: {
      method: 'GET',
      path: '/overview',
      query: paymentsOverviewQuerySchema,
      responses: { 200: paymentsOverviewSchema, ...readErrors },
      summary:
        "Payment overview for a legal entity: its released runs, and the selected run's obligation counts, credited share, held/failed/processing totals and active batch",
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    listProviders: {
      method: 'GET',
      path: '/providers',
      responses: { 200: paymentProviderListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Payment provider and bank connections with their channels and capabilities (never credentials)',
      metadata: routeMeta('payroll.payment:read'),
    },

    listBatches: {
      method: 'GET',
      path: '/batches',
      query: listPaymentBatchesQuerySchema,
      responses: { 200: paymentBatchListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List payment batches, filtered by legal entity, payroll run or state',
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    getBatch: {
      method: 'GET',
      path: '/batches/:batchId',
      pathParams: batchParams,
      responses: { 200: paymentBatchDetailSchema, ...readErrors },
      summary:
        "A payment batch's manifest: digest, beneficiaries, net amount, exclusions, authorisation, bank files and which actions the caller may take",
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    getBankFileDownload: {
      method: 'GET',
      path: '/batches/:batchId/files/:fileId/download',
      pathParams: bankFileParams,
      responses: {
        200: paymentFileLinkSchema,
        ...readErrors,
        /** The file was voided. */
        409: errorBodySchema,
      },
      summary:
        "A short-lived private link to download a batch's bank file (the download is recorded)",
      metadata: routeMeta('payroll.payment:transmit', { ai: 'hidden', salaryPin: 'others' }),
    },

    listObligations: {
      method: 'GET',
      path: '/obligations',
      query: listPaymentObligationsQuerySchema,
      responses: { 200: paymentObligationListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'List payment obligations (what each employee is owed) with masked destination and latest attempt, by batch, run, entity, state or name',
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    getObligation: {
      method: 'GET',
      path: '/obligations/:obligationId',
      pathParams: obligationParams,
      responses: { 200: paymentObligationDetailSchema, ...readErrors },
      summary:
        'One payment obligation with its destination snapshots, attempts, provider and reconciliation events, holds and whether it may be retried',
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    exportLedger: {
      method: 'GET',
      path: '/ledger/export',
      query: exportPaymentLedgerQuerySchema,
      responses: { 200: paymentLedgerExportSchema, ...readErrors },
      summary: 'Export the masked payment ledger of a legal entity, run or batch as CSV',
      metadata: routeMeta('payroll.payment:read', { ai: 'hidden', salaryPin: 'others' }),
    },

    listTeamPayments: {
      method: 'GET',
      path: '/team',
      query: teamPaymentsQuerySchema,
      responses: { 200: teamPaymentListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        "My team's salary payment status (paid, processing, failed, held) — no amounts or bank details",
      metadata: routeMeta('payroll.payment.team:read'),
    },

    listMyPayments: {
      method: 'GET',
      path: '/mine',
      responses: { 200: myPaymentListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'My salary and other payments, their status, and whether I can fix a failure with a bank change',
      metadata: routeMeta('payroll.payment.own:read'),
    },

    // --- provider connections ----------------------------------------------

    createProvider: {
      method: 'POST',
      path: '/providers',
      headers: idempotencyHeadersSchema,
      body: createPaymentProviderBodySchema,
      responses: {
        201: paymentProviderConnectionSchema,
        ...readErrors,
        /** Unknown adapter, or the adapter needs a company debit account that was not given. */
        412: errorBodySchema,
      },
      summary:
        'Configure a payment provider or bank connection for a legal entity, with its company debit account',
      metadata: routeMeta('payroll.payment:authorise'),
    },

    updateProvider: {
      method: 'PATCH',
      path: '/providers/:providerId',
      pathParams: providerParams,
      headers: idempotencyHeadersSchema,
      body: updatePaymentProviderBodySchema,
      responses: {
        200: paymentProviderConnectionSchema,
        ...readErrors,
        /** The debit account cannot be cleared on an adapter that requires it. */
        412: errorBodySchema,
      },
      summary:
        "Rename, enable or disable a payment connection, or change its company debit account",
      metadata: routeMeta('payroll.payment:authorise'),
    },

    // --- batch writes ------------------------------------------------------

    prepareBatch: {
      method: 'POST',
      path: '/batches',
      headers: idempotencyHeadersSchema,
      body: preparePaymentBatchBodySchema,
      responses: {
        201: paymentBatchDetailSchema,
        ...readErrors,
        /** An obligation is already claimed by another active batch. */
        409: errorBodySchema,
        /** The run is not released, its payments are tracked manually, the provider does not support the channel, or nothing is payable. */
        412: errorBodySchema,
      },
      summary:
        'Prepare a payment batch from a released payroll run or from named obligations, snapshotting each destination',
      metadata: routeMeta('payroll.payment:prepare', { salaryPin: 'others' }),
    },

    submitBatch: {
      method: 'POST',
      path: '/batches/:batchId/submit',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: submitPaymentBatchBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on, or its digest is not the one submitted. */
        409: errorBodySchema,
        /** The batch is not `prepared`. */
        412: errorBodySchema,
      },
      summary: 'Submit a prepared payment batch for authorisation',
      metadata: routeMeta('payroll.payment:prepare', { salaryPin: 'others' }),
    },

    authoriseBatch: {
      method: 'POST',
      path: '/batches/:batchId/authorisation',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: authorisePaymentBatchBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on, the digest is stale, or the caller prepared or submitted this content version. */
        409: errorBodySchema,
        /** Not pending authorisation, or step-up is required or invalid (`STEP_UP_REQUIRED`). */
        412: errorBodySchema,
      },
      summary: 'Authorise or reject the exact contents of a payment batch',
      metadata: routeMeta('payroll.payment:authorise', { ai: 'hidden', salaryPin: 'others' }),
    },

    cancelBatch: {
      method: 'POST',
      path: '/batches/:batchId/cancel',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: cancelPaymentBatchBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on. */
        409: errorBodySchema,
        /** Money may have left: the batch was exported or transmitted. */
        412: errorBodySchema,
      },
      summary:
        'Cancel a payment batch before anything was exported or transmitted, returning its obligations',
      metadata: routeMeta('payroll.payment:prepare', { salaryPin: 'others' }),
    },

    exportBankFile: {
      method: 'POST',
      path: '/batches/:batchId/export',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: exportBankFileBodySchema,
      responses: {
        200: exportBankFileResultSchema,
        ...readErrors,
        /** The batch moved on. */
        409: errorBodySchema,
        /** Not authorised, not a bank-file batch, or its destinations changed since authorisation. */
        412: errorBodySchema,
      },
      summary: "Generate the bank file of an authorised batch in the bank's agreed format",
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    confirmBatchUpload: {
      method: 'POST',
      path: '/batches/:batchId/upload-confirmation',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: confirmBatchUploadBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on, or the file is not its current one. */
        409: errorBodySchema,
        /** The batch is not `exported`. */
        412: errorBodySchema,
      },
      summary:
        "Record that a batch's bank file was uploaded to the bank, with the bank's reference",
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    voidBatchExport: {
      method: 'POST',
      path: '/batches/:batchId/void-export',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: voidBatchExportBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on, or the file is not its current one. */
        409: errorBodySchema,
        /** The batch is not `exported` (an uploaded file cannot be voided). */
        412: errorBodySchema,
      },
      summary:
        'Void an exported bank file that was not uploaded, invalidating the batch so it can be re-prepared',
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    transmitBatch: {
      method: 'POST',
      path: '/batches/:batchId/transmit',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: batchVersionBodySchema,
      responses: {
        200: paymentBatchDetailSchema,
        ...readErrors,
        /** The batch moved on. */
        409: errorBodySchema,
        /** Not authorised, not an API/UPI batch, the provider connection is not active, or destinations changed. */
        412: errorBodySchema,
      },
      summary: 'Send an authorised batch to the payment provider (queued to a worker)',
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    // --- obligation writes -------------------------------------------------

    retryObligation: {
      method: 'POST',
      path: '/obligations/:obligationId/retry',
      pathParams: obligationParams,
      headers: idempotencyHeadersSchema,
      body: retryPaymentObligationBodySchema,
      responses: {
        200: paymentObligationDetailSchema,
        ...readErrors,
        /** The latest attempt is not the one named, the verdict refuses a retry, or the destination changed (`needs_new_batch`: prepare a retry batch instead). */
        409: errorBodySchema,
      },
      summary: 'Retry a definitively failed payment to the destination its batch authorised',
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    requestStatusCheck: {
      method: 'POST',
      path: '/obligations/:obligationId/status-check',
      pathParams: obligationParams,
      headers: idempotencyHeadersSchema,
      body: paymentStatusCheckBodySchema,
      responses: {
        200: paymentObligationDetailSchema,
        ...readErrors,
        /** The attempt is already definitive. */
        409: errorBodySchema,
        /** The provider cannot be asked for a status. */
        412: errorBodySchema,
      },
      summary:
        'Ask the payment provider for the status of an attempt whose outcome is unknown or pending',
      metadata: routeMeta('payroll.payment:transmit', { salaryPin: 'others' }),
    },

    reconcileObligation: {
      method: 'POST',
      path: '/obligations/:obligationId/reconciliation',
      pathParams: obligationParams,
      headers: idempotencyHeadersSchema,
      body: reconcilePaymentObligationBodySchema,
      responses: {
        200: paymentObligationDetailSchema,
        ...readErrors,
        /** The attempt is not the obligation's, or already has this outcome. */
        409: errorBodySchema,
        /** The attempt was never submitted, so there is nothing to reconcile. */
        412: errorBodySchema,
      },
      summary:
        'Record a payment outcome confirmed outside the system, with the bank reference and evidence',
      metadata: routeMeta('payroll.payment:reconcile', { salaryPin: 'others' }),
    },

    importBankResponse: {
      method: 'POST',
      path: '/batches/:batchId/bank-response',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: importBankResponseBodySchema,
      responses: {
        200: importBankResponseResultSchema,
        ...readErrors,
        /** The batch was never submitted to the bank. */
        412: errorBodySchema,
      },
      summary:
        "Import the bank's response for a batch: mark each employee's payment credited or failed, skipping rows that cannot apply",
      metadata: routeMeta('payroll.payment:reconcile', { ai: 'hidden', salaryPin: 'others' }),
    },

    // --- salary holds ------------------------------------------------------

    listHolds: {
      method: 'GET',
      path: '/holds',
      query: listSalaryHoldsQuerySchema,
      responses: { 200: paymentSalaryHoldListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'List salary holds (managers see only holds they requested or for their team, without amounts)',
      metadata: routeMeta(['payroll.payment:read', 'payroll.hold:request'], { salaryPin: 'others' }),
    },

    requestHold: {
      method: 'POST',
      path: '/holds',
      headers: idempotencyHeadersSchema,
      body: requestSalaryHoldBodySchema,
      responses: {
        201: paymentSalaryHoldDetailSchema,
        ...readErrors,
        /** The employee already has an open or active hold. */
        409: errorBodySchema,
      },
      summary: "Request a hold on an employee's salary payment, with the reason",
      metadata: routeMeta('payroll.hold:request', { salaryPin: 'others' }),
    },

    getHold: {
      method: 'GET',
      path: '/holds/:holdId',
      pathParams: holdParams,
      responses: { 200: paymentSalaryHoldDetailSchema, ...readErrors },
      summary: 'One salary hold with its history',
      metadata: routeMeta(['payroll.payment:read', 'payroll.hold:request'], { salaryPin: 'others' }),
    },

    prepareHold: {
      method: 'POST',
      path: '/holds/:holdId/prepare',
      pathParams: holdParams,
      headers: idempotencyHeadersSchema,
      body: prepareSalaryHoldBodySchema,
      responses: {
        200: paymentSalaryHoldDetailSchema,
        ...readErrors,
        /** The hold moved on. */
        409: errorBodySchema,
        /** The hold is not `requested`. */
        412: errorBodySchema,
      },
      summary: 'Prepare a requested salary hold and send it for approval',
      metadata: routeMeta('payroll.hold:manage', { salaryPin: 'others' }),
    },

    decideHold: {
      method: 'POST',
      path: '/holds/:holdId/decision',
      pathParams: holdParams,
      headers: idempotencyHeadersSchema,
      body: decideSalaryHoldBodySchema,
      responses: {
        200: paymentSalaryHoldDetailSchema,
        ...readErrors,
        /** The hold moved on, or the caller requested or prepared it. */
        409: errorBodySchema,
        /** The hold is not pending approval. */
        412: errorBodySchema,
      },
      summary: 'Approve (put into effect) or reject a salary hold',
      metadata: routeMeta('payroll.hold:approve', { salaryPin: 'others' }),
    },

    withdrawHold: {
      method: 'POST',
      path: '/holds/:holdId/withdraw',
      pathParams: holdParams,
      headers: idempotencyHeadersSchema,
      body: withdrawSalaryHoldBodySchema,
      responses: {
        200: paymentSalaryHoldDetailSchema,
        ...readErrors,
        /** The hold moved on. */
        409: errorBodySchema,
        /** The hold is already active or closed (an active hold is released, not withdrawn). */
        412: errorBodySchema,
      },
      summary: 'Withdraw a salary hold that has not yet taken effect',
      metadata: routeMeta(['payroll.hold:request', 'payroll.hold:manage'], { salaryPin: 'others' }),
    },

    releaseHold: {
      method: 'POST',
      path: '/holds/:holdId/release',
      pathParams: holdParams,
      headers: idempotencyHeadersSchema,
      body: releaseSalaryHoldRequestSchema,
      responses: {
        200: releaseSalaryHoldResultSchema,
        ...readErrors,
        /** The hold moved on. */
        409: errorBodySchema,
        /** The hold is not active. */
        412: errorBodySchema,
      },
      summary: "Release an active salary hold and reassess the employee's held payments",
      metadata: routeMeta('payroll.hold:approve', { salaryPin: 'others' }),
    },

    // --- off-cycle payments ------------------------------------------------

    listOffCycle: {
      method: 'GET',
      path: '/off-cycle',
      query: listOffCyclePaymentsQuerySchema,
      responses: { 200: offCyclePaymentListSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'List off-cycle payments (arrears, bonus, corrections) with their calculation and approval',
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    createOffCycle: {
      method: 'POST',
      path: '/off-cycle',
      headers: idempotencyHeadersSchema,
      body: createOffCyclePaymentBodySchema,
      responses: {
        201: offCyclePaymentSchema,
        ...readErrors,
        /** The employment is not active in a legal entity that pays in this currency, or a component is unknown. */
        412: errorBodySchema,
      },
      summary: 'Create a draft off-cycle payment for an employee with its reason and components',
      metadata: routeMeta('payroll.offcycle:manage', { salaryPin: 'others' }),
    },

    getOffCycle: {
      method: 'GET',
      path: '/off-cycle/:offCycleId',
      pathParams: offCycleParams,
      responses: { 200: offCyclePaymentSchema, ...readErrors },
      summary: 'One off-cycle payment with its calculation, approval and payment obligation',
      metadata: routeMeta('payroll.payment:read', { salaryPin: 'others' }),
    },

    updateOffCycle: {
      method: 'PATCH',
      path: '/off-cycle/:offCycleId',
      pathParams: offCycleParams,
      headers: idempotencyHeadersSchema,
      body: updateOffCyclePaymentBodySchema,
      responses: {
        200: offCyclePaymentSchema,
        ...readErrors,
        /** The off-cycle payment moved on. */
        409: errorBodySchema,
        /** It is not `draft` or `rejected`. */
        412: errorBodySchema,
      },
      summary: 'Edit a draft or rejected off-cycle payment (discards its calculation)',
      metadata: routeMeta('payroll.offcycle:manage', { salaryPin: 'others' }),
    },

    calculateOffCycle: {
      method: 'POST',
      path: '/off-cycle/:offCycleId/calculate',
      pathParams: offCycleParams,
      headers: idempotencyHeadersSchema,
      body: offCycleVersionBodySchema,
      responses: {
        200: offCyclePaymentSchema,
        ...readErrors,
        /** The off-cycle payment moved on. */
        409: errorBodySchema,
        /** It is not `draft` or `calculated`. */
        412: errorBodySchema,
      },
      summary: 'Calculate tax and deductions for an off-cycle payment in the payroll engine',
      metadata: routeMeta('payroll.offcycle:manage', { salaryPin: 'others' }),
    },

    submitOffCycle: {
      method: 'POST',
      path: '/off-cycle/:offCycleId/submit',
      pathParams: offCycleParams,
      headers: idempotencyHeadersSchema,
      body: submitOffCyclePaymentBodySchema,
      responses: {
        200: offCyclePaymentSchema,
        ...readErrors,
        /** The off-cycle payment moved on, or the calculation is not the current one. */
        409: errorBodySchema,
        /** It is not `calculated`, or its calculation has problems. */
        412: errorBodySchema,
      },
      summary: 'Submit a calculated off-cycle payment for approval',
      metadata: routeMeta('payroll.offcycle:manage', { salaryPin: 'others' }),
    },

    decideOffCycle: {
      method: 'POST',
      path: '/off-cycle/:offCycleId/decision',
      pathParams: offCycleParams,
      headers: idempotencyHeadersSchema,
      body: decideOffCyclePaymentBodySchema,
      responses: {
        200: offCyclePaymentSchema,
        ...readErrors,
        /** It moved on, the calculation is not the one submitted, or the caller prepared or submitted it. */
        409: errorBodySchema,
        /** It is not pending approval. */
        412: errorBodySchema,
      },
      summary: 'Approve or reject the exact calculation of an off-cycle payment',
      metadata: routeMeta('payroll.offcycle:approve', { salaryPin: 'others' }),
    },

    cancelOffCycle: {
      method: 'POST',
      path: '/off-cycle/:offCycleId/cancel',
      pathParams: offCycleParams,
      headers: idempotencyHeadersSchema,
      body: cancelOffCyclePaymentBodySchema,
      responses: {
        200: offCyclePaymentSchema,
        ...readErrors,
        /** The off-cycle payment moved on. */
        409: errorBodySchema,
        /** It is already approved, paid or closed. */
        412: errorBodySchema,
      },
      summary: 'Cancel an off-cycle payment that has not been approved',
      metadata: routeMeta('payroll.offcycle:manage', { salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/payroll/payments' },
);
